import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { DateTime } from "luxon";
import { Container } from "../components/Container";
import { Card } from "../components/Card";
import { Input } from "../components/Input";
import { Button } from "../components/Button";
import { InfoBox } from "../components/InfoBox";
import { EmptyState } from "../components/EmptyState";
import { LoadingContainer } from "../components/LoadingContainer";
import { StatBox } from "../components/StatBox";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Title } from "../components/Title";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "../components/Table";
import { ResellerInvoiceResultPanel } from "./components/ResellerInvoiceResultPanel";
import {
  useGenerateResellerInvoicesMutation,
  useGetAdminResellerInvoicesQuery,
  useSendResellerInvoiceToBokioMutation,
} from "../query/adminResellerInvoicesQuery";
import { formatDate } from "../utils/dateUtils";
import { formatMoney } from "../utils/formatMoney";
import { SALES_TIMEZONE } from "../utils/dailySalesUtils";
import { sumMoney } from "./components/kickback/kickbackTotals";
import { spacing } from "@styling/sizing";
import colors from "@styling/colors";
import type { Money } from "@KassaroAB/shared/types/money";
import type { MultiCurrencyMoney } from "@KassaroAB/shared/types/multiCurrencyMoney";
import {
  addMultiCurrencyMoney,
  zeroMultiCurrencyMoney,
} from "@KassaroAB/shared/utils/multiCurrencyMoney";
import type { ResellerId } from "@KassaroAB/shared/types/ids";
import type { RestResellerInvoiceData } from "@KassaroAB/shared/types/invoices/resellerInvoice";

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing.x2}px;
  margin-bottom: ${spacing.x4}px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing.x2}px;
`;

const PeriodLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${spacing.x2}px;
`;

const AutoWidthInput = styled(Input)`
  width: auto;
`;

const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x4}px;
`;

const StatRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.x3}px;
`;

const RowActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${spacing.x1}px;
`;

const StatusText = styled.span<{ $status: InvoiceStatus }>`
  font-weight: 500;
  color: ${({ $status }) => statusColor[$status]};
`;

type InvoiceStatus = "notGenerated" | "generated" | "sent";

const statusColor: Record<InvoiceStatus, string> = {
  notGenerated: colors.textMuted,
  generated: colors.text,
  sent: colors.successText,
};

const currentMonth = () =>
  DateTime.now().setZone(SALES_TIMEZONE).toFormat("yyyy-MM");

const statusOf = (invoice: RestResellerInvoiceData | null): InvoiceStatus => {
  if (!invoice) return "notGenerated";
  return invoice.sent ? "sent" : "generated";
};

const formatMultiCurrency = (money: MultiCurrencyMoney) => {
  const totals = Object.values(money.currencies).filter(
    (amount): amount is Money => !!amount,
  );
  if (totals.length === 0) return "–";
  return totals.map((total) => formatMoney(total)).join(" + ");
};

const formatTotals = (amounts: Money[]) =>
  formatMultiCurrency(
    amounts.reduce(addMultiCurrencyMoney, zeroMultiCurrencyMoney()),
  );

export const AdminResellerInvoicingView = () => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState(currentMonth);
  const [search, setSearch] = useState("");
  const [progress, setProgress] = useState(0);
  const [selectedResellerId, setSelectedResellerId] =
    useState<ResellerId | null>(null);
  const [sendResellerId, setSendResellerId] = useState<ResellerId | null>(null);
  const {
    data: items,
    isLoading,
    error: loadError,
    refetch,
  } = useGetAdminResellerInvoicesQuery(period);
  const generate = useGenerateResellerInvoicesMutation(period, setProgress);
  const send = useSendResellerInvoiceToBokioMutation(period);
  const isBusy = generate.isPending || send.isPending;

  const rows = items ?? [];
  const query = search.trim().toLowerCase();
  const visibleRows = query
    ? rows.filter((row) => row.resellerName.toLowerCase().includes(query))
    : rows;
  const invoices = rows.flatMap((row) => (row.invoice ? [row.invoice] : []));
  const sendRow = rows.find((row) => row.resellerId === sendResellerId);
  const sendFailedName = rows.find(
    (row) => row.resellerId === send.variables,
  )?.resellerName;
  const selectedRow = rows.find((row) => row.resellerId === selectedResellerId);
  const unsentResellerIds = rows
    .filter((row) => !row.invoice?.sent)
    .map((row) => row.resellerId);
  const failedNames = (generate.data ?? []).map(
    (resellerId) =>
      rows.find((row) => row.resellerId === resellerId)?.resellerName ??
      resellerId,
  );

  const selectPeriod = (nextPeriod: string) => {
    if (isBusy) return;
    generate.reset();
    send.reset();
    setSelectedResellerId(null);
    setPeriod(nextPeriod);
  };

  return (
    <Container>
      <PageHeader>
        <Title>{t("adminResellerInvoicing.title")}</Title>
        <HeaderActions>
          <PeriodLabel>
            {t("adminResellerInvoicing.period")}
            <AutoWidthInput
              type="month"
              value={period}
              max={currentMonth()}
              disabled={isBusy}
              onChange={(event) => {
                if (event.target.value) {
                  selectPeriod(event.target.value);
                }
              }}
            />
          </PeriodLabel>
          <Button
            onClick={() => generate.mutate(unsentResellerIds)}
            disabled={isBusy || isLoading || unsentResellerIds.length === 0}
          >
            {generate.isPending
              ? t("adminResellerInvoicing.generatingProgress", {
                  done: progress,
                  total: generate.variables?.length ?? 0,
                })
              : t("adminResellerInvoicing.generateAll", {
                  count: unsentResellerIds.length,
                })}
          </Button>
        </HeaderActions>
      </PageHeader>

      <Sections>
        <InfoBox variant="info">
          {t("adminResellerInvoicing.description")}
        </InfoBox>
        {failedNames.length > 0 && (
          <InfoBox variant="error">
            {t("adminResellerInvoicing.generateFailed", {
              names: failedNames.join(", "),
            })}
          </InfoBox>
        )}
        {send.error && (
          <InfoBox variant="error">
            {t("adminResellerInvoicing.sendFailed", {
              name: sendFailedName ?? send.variables,
              message: send.error.message,
            })}
          </InfoBox>
        )}

        {isLoading ? (
          <LoadingContainer message={t("adminResellerInvoicing.loading")} />
        ) : loadError ? (
          <InfoBox variant="error">
            {t("adminResellerInvoicing.loadError")} {loadError.message}{" "}
            <Button $variant="outlined" $small onClick={() => refetch()}>
              {t("common.retry")}
            </Button>
          </InfoBox>
        ) : (
          <>
            <StatRow>
              <StatBox
                label={t("adminResellerInvoicing.stats.total")}
                value={formatTotals(
                  invoices.map((invoice) => invoice.totalAmount),
                )}
              />
              <StatBox
                label={t("adminResellerInvoicing.stats.kickback")}
                value={formatTotals(
                  invoices.flatMap((invoice) =>
                    invoice.kickbackLineItems.map((line) => line.amount),
                  ),
                )}
              />
              <StatBox
                label={t("adminResellerInvoicing.stats.generated")}
                value={`${invoices.length} / ${rows.length}`}
              />
              <StatBox
                label={t("adminResellerInvoicing.stats.sent")}
                value={`${invoices.filter((invoice) => invoice.sent).length} / ${rows.length}`}
              />
            </StatRow>

            <AutoWidthInput
              type="search"
              value={search}
              placeholder={t("adminResellerInvoicing.search")}
              onChange={(event) => setSearch(event.target.value)}
            />

            <Card>
              {visibleRows.length === 0 ? (
                <EmptyState message={t("adminResellerInvoicing.empty")} />
              ) : (
                <Table minWidth="760px">
                  <TableHeader>
                    <tr>
                      <TableHeaderCell>
                        {t("adminResellerInvoicing.table.reseller")}
                      </TableHeaderCell>
                      <TableHeaderCell>
                        {t("adminResellerInvoicing.table.status")}
                      </TableHeaderCell>
                      <TableHeaderCell>
                        {t("adminResellerInvoicing.table.generatedAt")}
                      </TableHeaderCell>
                      <TableHeaderCell align="right">
                        {t("adminResellerInvoicing.table.stores")}
                      </TableHeaderCell>
                      <TableHeaderCell align="right">
                        {t("adminResellerInvoicing.table.kickback")}
                      </TableHeaderCell>
                      <TableHeaderCell align="right">
                        {t("adminResellerInvoicing.table.total")}
                      </TableHeaderCell>
                      <TableHeaderCell align="right">
                        {t("adminResellerInvoicing.table.actions")}
                      </TableHeaderCell>
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {visibleRows.map((row) => {
                      const { invoice } = row;
                      const status = statusOf(invoice);
                      return (
                        <TableRow
                          key={row.resellerId}
                          clickable={!!invoice}
                          active={selectedResellerId === row.resellerId}
                          onClick={() =>
                            invoice && setSelectedResellerId(row.resellerId)
                          }
                        >
                          <TableCell>{row.resellerName}</TableCell>
                          <TableCell noWrap>
                            <StatusText $status={status}>
                              {t(`adminResellerInvoicing.status.${status}`)}
                            </StatusText>
                          </TableCell>
                          <TableCell noWrap>
                            {invoice ? formatDate(invoice.generatedAt) : "–"}
                          </TableCell>
                          <TableCell align="right" noWrap>
                            {invoice
                              ? new Set(
                                  invoice.lineItems.map((line) => line.storeId),
                                ).size
                              : "–"}
                          </TableCell>
                          <TableCell align="right" noWrap>
                            {invoice?.kickbackLineItems.length
                              ? formatMoney(
                                  sumMoney(
                                    invoice.kickbackLineItems.map(
                                      (line) => line.amount,
                                    ),
                                  ),
                                )
                              : "–"}
                          </TableCell>
                          <TableCell align="right" noWrap>
                            {invoice ? formatMoney(invoice.totalAmount) : "–"}
                          </TableCell>
                          <TableCell align="right" noWrap>
                            {!invoice?.sent && (
                              <RowActions>
                                <Button
                                  $variant="outlined"
                                  $small
                                  disabled={isBusy}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    generate.mutate([row.resellerId]);
                                  }}
                                >
                                  {invoice
                                    ? t("adminResellerInvoicing.regenerate")
                                    : t("adminResellerInvoicing.generate")}
                                </Button>
                                {invoice && (
                                  <Button
                                    $small
                                    disabled={isBusy}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setSendResellerId(row.resellerId);
                                    }}
                                  >
                                    {send.isPending &&
                                    send.variables === row.resellerId
                                      ? t("invoice.markingAsSent")
                                      : t("adminResellerInvoicing.sendToBokio")}
                                  </Button>
                                )}
                              </RowActions>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </Card>
          </>
        )}
      </Sections>

      <ConfirmDialog
        isOpen={!!sendRow?.invoice}
        title={t("adminResellerInvoicing.sendConfirm.title")}
        message={t("adminResellerInvoicing.sendConfirm.message", {
          name: sendRow?.resellerName,
          total: formatMoney(sendRow?.invoice?.totalAmount),
        })}
        confirmLabel={t("adminResellerInvoicing.sendConfirm.confirm")}
        cancelLabel={t("adminResellerInvoicing.sendConfirm.cancel")}
        onConfirm={() => {
          if (sendResellerId) {
            send.mutate(sendResellerId);
          }
          setSendResellerId(null);
        }}
        onCancel={() => setSendResellerId(null)}
      />

      {selectedRow?.invoice && (
        <ResellerInvoiceResultPanel
          invoice={selectedRow.invoice}
          resellerId={selectedRow.resellerId}
          onClose={() => setSelectedResellerId(null)}
        />
      )}
    </Container>
  );
};
